import { cookieNames, stashPlatformConstant } from './native-bridge';
import { getCookie } from './cookies';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';

export const isMobileWidth = (breakpoint: number): boolean => {
	// Use matchMedia for better performance and accuracy
	if (window.matchMedia) {
		return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
	}
	// Fallback for older browsers
	return window.innerWidth <= breakpoint;
};

// a hook for isMobileWidth that re-renders when the window is resized
export const useMobileWidth = (breakpoint: number): boolean => {
	const [isMobile, setIsMobile] = useState(() => isMobileWidth(breakpoint));
	
	useEffect(() => {
		// Use MediaQueryList for better performance than resize events
		const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
		
		const handleChange = (e: MediaQueryListEvent) => {
			setIsMobile(e.matches);
		};
		
		// Set initial state and listen for changes
		setIsMobile(mediaQuery.matches);
		mediaQuery.addEventListener('change', handleChange);
		
		return () => {
			mediaQuery.removeEventListener('change', handleChange);
		};
	}, [breakpoint]);
	
	return isMobile;
};

/**
 * Generic function to check for mobile device based on user agent.
 * @returns {boolean}
 */
export const isMobileDevice = (): boolean => {
	const userAgent = navigator.userAgent.toLowerCase();
	return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

/**
 * Generic function to check for mobile viewport or device with configurable breakpoint.
 * @param breakpoint - The pixel width breakpoint to consider as mobile (default: 768)
 * @returns {boolean}
 */
export const isMobileWeb = (breakpoint = 768): boolean => {
	return isMobileDevice() || isMobileWidth(breakpoint);
};

/**
 * Checks for native web view
 * @returns {boolean}
 */
export const isNativeWebview = (): boolean => {
	const stashPlatformVal = getCookie(cookieNames.NATIVE_PLATFORM);
	return (
		stashPlatformVal === stashPlatformConstant.ANDROID ||
		stashPlatformVal === stashPlatformConstant.IOS
	);
};

/**
 * Pure UA check for Facebook / Instagram in-app browser. Safe to call during
 * render (no telemetry side effects).
 */
export const isFBorIGWebViewUserAgent = (): boolean => {
	const userAgent = navigator.userAgent || '';
	return (
		userAgent.indexOf('FBAN') > -1 ||
		userAgent.indexOf('FBAV') > -1 ||
		userAgent.indexOf('Instagram') > -1
	);
};

/**
 * Names the app hosting the in-app browser, for copy that has to tell the user
 * which app to escape. Falls back to a generic noun when the UA is neither.
 */
export const getIabAppName = (): string => {
	const userAgent = navigator.userAgent || '';
	if (userAgent.indexOf('Instagram') > -1) {
		return 'Instagram';
	}
	if (userAgent.indexOf('FBAN') > -1 || userAgent.indexOf('FBAV') > -1) {
		return 'Facebook';
	}
	return 'This app';
};

/**
 * Detects Facebook / Instagram WebView and emits a Datadog RUM action.
 * Prefer `isFBorIGWebViewUserAgent` during render; call this once in an effect
 * when the RUM signal is needed.
 */
export const isFBorIGWebView = (): boolean => {
	const userAgent = navigator.userAgent || '';
	const isFBorIG = isFBorIGWebViewUserAgent();
	datadogRum.addAction('web-reg custom user agent', {
		ua: userAgent,
		isFBorIG: isFBorIG,
	});
	return isFBorIG;
};

/**
 * Checks if the device is Android
 * @returns {boolean}
 */
export const isAndroid = (): boolean => {
	return navigator.userAgent.includes('Android');
};

/**
 * Checks if the device is iOS
 * @returns {boolean}
 */
export const isIOS = (): boolean => {
	return (
		navigator.userAgent.includes('iPhone') ||
		navigator.userAgent.includes('iPad') ||
		navigator.userAgent.includes('iPod')
	);
};

/**
 * Transforms URL to open in native browser
 * @param urlWithScheme - The URL with scheme (e.g., 'https://example.com')
 * @returns {string}
 */
export const transformURLToOpenInNativeBrowser = (urlWithScheme: string): string => {
	if (isAndroid()) {
		const urlWithoutScheme = urlWithScheme.replace(/^https:\/\//, '');
		return `intent://${urlWithoutScheme}#Intent;scheme=https;S.browser_fallback_url=${encodeURIComponent(
			urlWithScheme
		)};end`;
	}

	if (isIOS()) {
		return `x-safari-${urlWithScheme}`;
	}

	return urlWithScheme;
};

